import { useEffect, useMemo, useState } from "react";

import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { Button } from "V2.0/common/button";
import useEvalRuns from "./useEvalRuns";
import AutomatedTesting from "./AutomatedTesting";
import { useIsIndiaResidency } from "../ModelSettings/GPTResponseModelCategorized/useIsIndiaResidency";

export default EvalRuns;

function EvalRuns(props) {
  const {
    workspaceId,
    assistantId,
    showPremiumOptions,
    enablePremiumOptions,
    locked,
    voiceSettings,
  } = props;

  const showIndiaDeployedModels = useIsIndiaResidency(
    voiceSettings?.audioDataResidency,
  );

  const [newTestSignal, setNewTestSignal] = useState<number>(0);
  const [copyTestSignal, setCopyTestSignal] = useState<number>(0);
  const [testingView, setTestingView] = useState<string>("templates");
  const { evalRuns, pagination, isEvalRunsLoading, fetchEvalRuns, createEvalRun, runEvalTemplate, removeEvalRun } =
    useEvalRuns(workspaceId, assistantId);

  useEffect(() => {
    if (workspaceId && assistantId) {
      fetchEvalRuns({ page: 1, limit: 20 }, true);
    }
  }, [workspaceId, assistantId]);

  const handlePageChange = (page) => {
    fetchEvalRuns({ page, limit: 20 }, true);
  };

  const hasRunInFlight = useMemo(() => {
    return (evalRuns ?? []).some(
      (evalRun) =>
        evalRun?.status === "pending" || evalRun?.status === "running",
    );
  }, [evalRuns]);

  useEffect(() => {
    if (!hasRunInFlight) return;

    const intervalId = setInterval(() => {
      fetchEvalRuns({ page: pagination.page, limit: 20 }, false);
    }, 15000);

    return () => clearInterval(intervalId);
  }, [hasRunInFlight, pagination.page]);

  const evalRunCreateProps = useMemo(() => {
    return {
      createEvalRun,
      showPremiumOptions,
      enablePremiumOptions,
      showIndiaDeployedModels,
    };
  }, [createEvalRun, showPremiumOptions, enablePremiumOptions, showIndiaDeployedModels]);

  return (
    <div className="flex-1 h-full min-h-0 flex flex-col p-8 gap-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-md font-semibold">Automated Testing</p>
        {!locked && testingView === "templates" && (
          <div className="flex items-center gap-x-1">
            <Button plain onClick={() => setCopyTestSignal((n) => n + 1)}>
              <DocumentDuplicateIcon />
              Copy from another assistant
            </Button>
            <Button outline onClick={() => setNewTestSignal((n) => n + 1)}>
              New test
            </Button>
          </div>
        )}
      </div>

      <AutomatedTesting
        {...evalRunCreateProps}
        locked={locked}
        runEvalTemplate={runEvalTemplate}
        newTestSignal={newTestSignal}
        copyTestSignal={copyTestSignal}
        onViewChange={setTestingView}
        evalRuns={evalRuns}
        pagination={pagination}
        workspaceId={workspaceId}
        assistantId={assistantId}
        onPageChange={handlePageChange}
        isEvalRunsLoading={isEvalRunsLoading}
      />
    </div>
  );
}
